// src/pages/ItemAdjustment/components/CreateItemAdjustmentForm.jsx
import React, { useEffect, useState } from "react";
import {
  Form,
  Card,
  Row,
  Col,
  Input,
  DatePicker,
  Button,
  Typography,
  Flex,
  Grid,
  Drawer,
  Tooltip,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import ItemAdjustmentItemsTable from "./ItemAdjustmentItemsTable";
import ItemAdjustmentFooter from "./ItemAdjustmentFooter";
import {
  setFormField,
  calculateTotals,
  setResetAdjustment,
} from "../itemAdjustmentSlice";
import dayjs from "dayjs";
import { fetchStockForItem, fetchBatchesForItem } from "../itemAdjustmentThunk";
import InputField from "../../../components/Input/Input";
import { accessChecker, getAccessType } from "../../../lib/functions";
import { useNavigate } from "react-router-dom";
import useMigrationDate from "../../../hooks/useMigrationDate";
import { ArrowLeftOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { selectAuthInfo } from "../../Auth/authSelectors";
import { Checkbox } from 'antd';

const { Title } = Typography;
const { useBreakpoint } = Grid;

const CreateItemAdjustmentForm = ({
  form,
  onSave,
  voucherNo,
  itemsList,
  footerValues,
}) => {
  const dispatch = useDispatch();
  const { entryDate, batches, stock, isTatmeenProduct, a_id } = useSelector((s) => s.itemAdjustment);
  const screens = useBreakpoint();
  let [access_type, access_type_set] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);

  const authInfo = useSelector(selectAuthInfo);
  const isEditMode = !!a_id;

  const isTatmeenUAE =
    authInfo?.is_tatmeen_enabled == "yes" &&
    authInfo?.country == "United Arab Emirates" && (authInfo?.subscription == 'PharmaERP_Pro' || authInfo?.subscription == 'PharmaERP_Standard' || authInfo?.subscription == 'PharmaERP_Basic');

  const hasTatmeenEntryAccess = accessChecker("tatmeen_entry");
  console.log(isTatmeenUAE, hasTatmeenEntryAccess, "isTatmeenUAE");

  useEffect(() => {
    const access = getAccessType("item_adjustment_entry");
    access_type_set(access);
  }, []);

  const navigate = useNavigate();

  // ✅ Recalculate totals whenever itemsList changes
  useEffect(() => {
    dispatch(calculateTotals());

  }, [itemsList, dispatch]);

  useEffect(() => {
    if (voucherNo) {
      form.setFieldsValue({ voucher_no: voucherNo });
    }
  }, [voucherNo, form]);

  const onSelectItem = (item, rowKey) => {
    const warehouseId = 0;

    // Reset batches for this product
    dispatch(
      setFormField({
        field: "batches",
        value: { ...batches, [item.sale_id]: [] },
      })
    );

    // Fetch stock for the product
    dispatch(fetchStockForItem({ itemId: item.sale_id, warehouseId }))
      .unwrap()
      .then(({ data }) => {
        dispatch(
          setFormField({
            field: "itemsList",
            value: itemsList.map((r) =>
              r.key === rowKey
                ? {
                  ...r,
                  item_id: item.sale_id,
                  product: item.display_text,
                  current_qty: Number(data.current_qty || 0),
                  // ✅ Scenario 1: No batch selected initially
                  // available_qty = loose_qty
                  available_qty: Number(data.loose_qty || 0),
                  loose_qty: Number(data.loose_qty || 0),
                  batch_controlled_qty: Number(data.batch_controlled_qty || 0),
                  has_batch_serial: data.has_batch_serial,
                  batch_id: null,
                  batch_name: "",
                  serial_mode: "unit",
                  rate: (data.average_rate ?? 0) * (data.conversion ?? 0),
                  quantity: 0, // ✅ Start with 0, not 1
                  total: 0,
                }
                : r
            ),
          })
        );
      });

    // Fetch batch list for that product
    dispatch(fetchBatchesForItem({ itemId: item.sale_id, warehouseId }));
  };

  const { disabledDate } = useMigrationDate();
  return (
    <div className="item-adjustment-form">
      <Flex
        justify="space-between"
        align="center"
        style={{
          marginBottom: !screens.md ? "16px" : "32px",
        }}
      >
        <Flex align="center" gap={8}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
            onClick={() => navigate(-1)}
            data-testid="item-adjustment-back-button"
          />
          <Title
            level={!screens.md ? 4 : 2}
            style={{
              margin: 0,
              whiteSpace: "normal",
              wordBreak: "break-word",
              textAlign: !screens.md ? "center" : "left",
            }}
          >
            Item Adjustment Entry
          </Title>
        </Flex>

        <Tooltip title="Help">
          <Button
            type="text"
            icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
            onClick={() => setHelpOpen(true)}
          />
        </Tooltip>
      </Flex>
      <Form
        form={form}
        layout="horizontal"
        onFinish={onSave}
        initialValues={{ variant: "filled" }}
        style={{ width: "100%", maxWidth: "1200px" }}
        // className="responsive-form"
        colon={false}
        labelCol={{
          span: 8,
        }}
        wrapperCol={{
          span: 18,
        }}
      >

        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          <Flex
            vertical
            gap={10}
            style={{
              width: !screens.md || !screens.xl ? "100%" : "40%",
            }}
          >
            <Form.Item label="Voucher No" name="voucher_no">
              <InputField data-testid="voucher_no" disabled value={voucherNo} />
            </Form.Item>
          </Flex>{" "}
          <Flex
            vertical
            gap={10}
            style={{
              width: !screens.md || !screens.xl ? "100%" : "40%",
            }}
          >
            <Form.Item label="Entry Date">
              <DatePicker
                style={{ width: "100%" }}
                value={entryDate ? dayjs(entryDate) : null}
                onChange={(date) =>
                  dispatch(
                    setFormField({
                      field: "entryDate",
                      value: date ? date.toISOString() : null,
                    })
                  )
                }
                disabledDate={disabledDate}
              />
            </Form.Item>
            {isTatmeenUAE && hasTatmeenEntryAccess && <>
              <Form.Item label="Tatmeen Product">
                <Checkbox
                  checked={isTatmeenProduct}
                  disabled={isEditMode}
                  onChange={(e) =>
                    dispatch(
                      setFormField({
                        field: 'isTatmeenProduct',
                        value: e.target.checked
                      })
                    )
                  }
                >Is Tatmeen Product
                </Checkbox>
              </Form.Item>
            </>}

          </Flex>{" "}
        </Flex>
        {/* <Row gutter={[16, 16]}> */}
        {/* Voucher No */}
        {/* <Col xs={24} sm={24} md={12} lg={12}></Col> */}

        {/* Entry Date */}
        {/* <Col xs={24} sm={24} md={12} lg={12}></Col> */}

        {/* ✅ Description moved to top */}
        {/* <Col xs={24} sm={24} md={12} lg={24}></Col> */}
        {/* </Row> */}

        {/* Items Table */}
        <Card
          bordered
          style={{ marginBottom: 24, marginTop: 24 }}
          title={
            <Title level={4} style={{ margin: 0 }}>
              Adjustment Items
            </Title>
          }
        >
          <ItemAdjustmentItemsTable
            itemsList={itemsList}
            onSelectItem={onSelectItem}
          />
        </Card>
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="end"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          <Flex
            vertical
            gap={10}
            style={{
              width: !screens.md || !screens.xl ? "100%" : "40%",
            }}
          >
            <Form.Item label="Description">
              <Input.TextArea
                rows={2}
                value={footerValues.narration}
                onChange={(e) =>
                  dispatch(
                    setFormField({
                      field: "footerValues",
                      value: { ...footerValues, narration: e.target.value },
                    })
                  )
                }
              />
            </Form.Item>
          </Flex>
        </Flex>
        {/* ✅ Show Total in footer */}
        <ItemAdjustmentFooter footerValues={footerValues} />

        {/* Buttons */}
        <Flex style={{ marginBottom: "32px", width: "25%" }} gap={32}>
          <Button
            size="large"
            shape="default"
            onClick={() => {
              dispatch(setResetAdjustment());
              form.resetFields();
              dispatch(
                setFormField({
                  field: "itemsList",
                  value: [{}],
                })
              );

              dispatch(setResetAdjustment());
            }}
            className="traic-button"
          >
            Reset
          </Button>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                className="traic-button"
                size="large"
                onClick={onSave}
              >
                Save
              </Button>
            </>
          )}
        </Flex>
      </Form>
      <Drawer
        title="Item Adjustment Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Item Adjustment Entry
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Item Adjustment module is used to update inventory stock
            quantities manually within the ERP system. It helps correct stock
            differences caused by damages, shortages, excess quantities,
            or stock verification processes.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Steps to Create Item Adjustment</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>Open <strong>Inventory → Item Adjustment</strong></li>
              <li>Verify voucher number and entry date</li>
              <li>Select the required item/product</li>
              <li>Check available stock quantity</li>
              <li>Enter adjusted quantity and rate</li>
              <li>Select batch details if applicable</li>
              <li>Add adjustment description or notes</li>
              <li>Review adjustment totals and stock impact</li>
              <li>Click <strong>Save</strong> to complete the adjustment entry</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Item adjustments directly affect inventory stock levels.</li>
              <li>Verify quantities carefully before saving adjustments.</li>
              <li>Batch selection may be required for batch-managed items.</li>
              <li>Adjustment entries are recorded for inventory tracking purposes.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default CreateItemAdjustmentForm;
